import React, { useEffect, useRef, useState } from 'react';
import { TeamOption } from '../types/auth/team';
import Spinner from './Spinner';

const PAGE_SIZE = 20;
const SCROLL_LOAD_THRESHOLD_PX = 48;
const LISTBOX_ID = 'team-search-listbox';

interface TeamSearchComboboxProps {
  query: string;
  setQuery: (value: string) => void;
  results: TeamOption[];
  loading: boolean;
  loadError?: string | null;
  onRetry?: () => void;
  showEmptyResults: boolean;
  teamNotListedMessage?: string;
  selectedTeam: TeamOption | null;
  onSelectedTeamChange: (team: TeamOption | null) => void;
  error?: string;
  disabled?: boolean;
}

function HighlightMatch({ text, query }: { text: string; query: string }) {
  const q = query.trim();
  if (!q) return <>{text}</>;

  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return <>{text}</>;

  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-cyan-100 text-cyan-900 font-semibold p-0">
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>
  );
}

const TeamSearchCombobox: React.FC<TeamSearchComboboxProps> = ({
  query,
  setQuery,
  results,
  loading,
  loadError,
  onRetry,
  showEmptyResults,
  teamNotListedMessage,
  selectedTeam,
  onSelectedTeamChange,
  error,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const fieldDisabled = disabled || loading || Boolean(loadError);
  const visibleResults = results.slice(0, visibleCount);
  const hasMore = results.length > visibleCount;
  const showDropdown = isOpen && query.trim().length >= 1 && !fieldDisabled;

  useEffect(() => {
    setActiveIndex(0);
    setVisibleCount(PAGE_SIZE);
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!showDropdown) return;
    const active = listRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    active?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, showDropdown]);

  const loadMore = () => {
    if (!hasMore) return;
    setVisibleCount((count) => count + PAGE_SIZE);
  };

  const handleListScroll = (e: React.UIEvent<HTMLUListElement>) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_LOAD_THRESHOLD_PX) {
      loadMore();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setIsOpen(true);

    if (selectedTeam && value !== selectedTeam.name) {
      onSelectedTeamChange(null);
    }
  };

  const handleSelect = (team: TeamOption) => {
    onSelectedTeamChange(team);
    setQuery(team.name);
    setIsOpen(false);
  };

  const handleClear = () => {
    setQuery('');
    onSelectedTeamChange(null);
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (fieldDisabled) return;

    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp') && query.trim().length >= 1) {
      setIsOpen(true);
      e.preventDefault();
      return;
    }

    if (!showDropdown) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (visibleResults.length === 0) return;
      if (activeIndex >= visibleResults.length - 1 && hasMore) {
        loadMore();
        setActiveIndex((i) => i + 1);
        return;
      }
      setActiveIndex((i) => Math.min(i + 1, visibleResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (visibleResults.length === 0) return;
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const team = visibleResults[activeIndex];
      if (team) handleSelect(team);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  const inputClasses = `w-full p-2 pr-16 border rounded-md ${
    error || loadError ? 'border-red-500' : 'border-gray-300'
  } focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:bg-gray-100 disabled:text-gray-500`;

  const placeholder = loading
    ? 'Loading colleges…'
    : loadError
      ? 'Colleges unavailable'
      : 'Search for your college...';

  return (
    <div className="relative w-full" ref={containerRef}>
      <label
        htmlFor="team-search"
        className='block text-sm font-medium text-gray-700 mb-1 after:content-["*"] after:ml-0.5 after:text-red-500'
      >
        Team
      </label>
      <div className="relative">
        <input
          id="team-search"
          type="text"
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls={LISTBOX_ID}
          aria-autocomplete="list"
          aria-activedescendant={
            showDropdown && visibleResults[activeIndex]
              ? `team-option-${visibleResults[activeIndex].id}`
              : undefined
          }
          className={inputClasses}
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onFocus={() => {
            if (!fieldDisabled && !selectedTeam) setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          disabled={fieldDisabled}
          autoComplete="off"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {loading && <Spinner size="xs" />}
          {selectedTeam && !disabled && !loading && (
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600 text-lg leading-none px-1"
              aria-label="Clear team selection"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {loadError && (
        <div className="mt-1 flex items-center gap-2">
          <p className="text-xs text-red-500">{loadError}</p>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="text-xs text-cyan-700 hover:text-cyan-900 underline font-medium"
            >
              Retry
            </button>
          )}
        </div>
      )}

      {error && !loadError && <p className="mt-1 text-xs text-red-500">{error}</p>}

      {selectedTeam?.team_enabled === false && (
        <p className="mt-1 text-xs text-gray-500">Will be set up when you register</p>
      )}

      {showDropdown && (
        <ul
          id={LISTBOX_ID}
          ref={listRef}
          role="listbox"
          className="absolute z-10 w-full mt-1 max-h-[20rem] overflow-auto bg-white border border-gray-300 rounded-md shadow-lg"
          onScroll={handleListScroll}
        >
          {visibleResults.length > 0 ? (
            visibleResults.map((team, index) => {
              const isActive = index === activeIndex;
              return (
                <li
                  id={`team-option-${team.id}`}
                  key={team.id}
                  role="option"
                  aria-selected={isActive}
                  data-active={isActive ? 'true' : undefined}
                  className={`px-4 py-2 cursor-pointer text-sm ${
                    isActive ? 'bg-cyan-50' : 'hover:bg-gray-100'
                  }`}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => handleSelect(team)}
                >
                  <HighlightMatch text={team.name} query={query} />
                </li>
              );
            })
          ) : (
            !loading &&
            showEmptyResults && (
              <li className="px-4 py-2 text-gray-600 text-sm">
                {teamNotListedMessage ||
                  "Can't find your college? Submit a team request below or contact us at info@athlytesports.com."}
              </li>
            )
          )}
        </ul>
      )}
    </div>
  );
};

export default TeamSearchCombobox;
